import React from 'react';
import FormInput from '@/components/UI/FormInput';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import { Button, Card, OptionBuyingStrategySelect } from '@/components/UI';
import {
    buildTradingStrategyLegDerivativeTypeUpdate,
    getNextTradingStrategyLegName,
    getTradingStrategyLegSummary,
    isRiskToStopSizingEligible,
    isVisibleTradingStrategyLeg,
    parseTradingStrategyLegDerivativeTypeInput,
    parseTradingStrategyLegOptionTypeInput,
    parseTradingStrategyLegEntryTransactionSizeTypeInput,
    parseTradingStrategyLegOpenTransactionTypeInput,
    type TradingStrategyFormData,
    type TradingStrategyLegFormData,
    type TradingStrategyLegSizingScope
} from '../tradingStrategyEditorSubmitPayloads';
import { MAXIMUM_RISK_TO_STOP_MINIMUM_QUANTITY, RISK_TO_STOP_INELIGIBLE_MESSAGE } from './helpers';

interface StrategyLegsSectionProps {
    legs: TradingStrategyLegFormData[];
    setFormData: React.Dispatch<React.SetStateAction<TradingStrategyFormData>>;
    setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    sizingScope: TradingStrategyLegSizingScope;
    validationErrors: Record<string, string>;
}

const StrategyLegsSection = ({ legs, setFormData, setValidationErrors, sizingScope, validationErrors }: StrategyLegsSectionProps) => {
    const handleLegChange = <Field extends keyof TradingStrategyLegFormData>(
        index: number,
        field: Field,
        value: TradingStrategyLegFormData[Field]
    ) => {
        setFormData((prev) => ({
            ...prev,
            legs: prev.legs.map((leg, i) => {
                if (i === index) {
                    return {
                        ...leg,
                        [field]: value,
                        // Mark existing legs as modified when any field changes
                        isModified: leg.id && !leg.isNew ? true : leg.isModified
                    };
                }

                return leg;
            })
        }));

        setValidationErrors((prev) => ({ ...prev, [`leg_${index}_${String(field)}`]: '' }));
    };

    const handleLegDerivativeTypeChange = (index: number, derivativeType: TradingStrategyLegFormData['derivativeType']) => {
        setFormData((prev) => ({
            ...prev,
            legs: prev.legs.map((leg, i) => {
                if (i !== index) return leg;

                return {
                    ...leg,
                    ...buildTradingStrategyLegDerivativeTypeUpdate(leg, derivativeType),
                    isModified: leg.id !== undefined && !leg.isNew ? true : leg.isModified
                };
            })
        }));

        setValidationErrors((prev) => ({
            ...prev,
            [`leg_${index}_autoBacktestOptionBuyingStrategiesOverride`]: '',
            [`leg_${index}_derivativeType`]: '',
            [`leg_${index}_optionType`]: '',
            [`leg_${index}_openTransactionType`]: ''
        }));
    };

    const addLeg = () => {
        setFormData((prev) => ({
            ...prev,
            legs: [
                ...prev.legs,
                {
                    name: getNextTradingStrategyLegName(prev.legs),
                    description: '',
                    optionType: null,
                    derivativeType: null,
                    openTransactionType: 'buy',
                    entryTransactionSizeType: 'percentageRiskCapital',
                    entryTransactionSizeValue: 100,
                    riskStopCaptureKey: '',
                    minimumQuantity: null,
                    isNew: true,
                    isModified: false,
                    isDeleted: false
                }
            ]
        }));
    };

    const removeLeg = (index: number) => {
        if (legs.filter(isVisibleTradingStrategyLeg).length > 1) {
            setFormData((prev) => ({
                ...prev,
                legs: prev.legs.map((leg, i) => {
                    if (i === index) {
                        // If it's a new leg, remove it completely
                        if (leg.isNew) {
                            return { ...leg, isDeleted: true };
                        }

                        // If it's an existing leg, mark it for deletion
                        return { ...leg, isDeleted: true };
                    }

                    return leg;
                })
            }));
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h4 className="text-base font-semibold text-white">Position</h4>
                    <p className="mt-1 max-w-[58ch] text-xs text-zinc-500">Choose the opening action, instrument, and size for each leg.</p>
                </div>
                <Button size="sm" variant="outline-dark" onClick={addLeg}>
                    Add leg
                </Button>
            </div>

            {legs.map((leg, index) => {
                // Skip rendering deleted legs
                if (!isVisibleTradingStrategyLeg(leg)) return null;

                // Calculate display index (excluding deleted legs)
                const displayIndex = legs.slice(0, index + 1).filter(isVisibleTradingStrategyLeg).length;
                const usesRiskToStop = leg.entryTransactionSizeType === 'percentageRiskToStop';
                const riskToStopEligible = isRiskToStopSizingEligible(sizingScope, leg);
                // A leg can lose eligibility from a change elsewhere (entity type, duration, derivative type). Its
                // stored choice stays visible and flagged from current state, so the message clears as soon as the
                // leg is valid again; validation blocks the save meanwhile.
                const sizeTypeError = usesRiskToStop && !riskToStopEligible ? RISK_TO_STOP_INELIGIBLE_MESSAGE : undefined;

                return (
                    <Card
                        headerActions={
                            legs.filter(isVisibleTradingStrategyLeg).length > 1 ? (
                                <Button size="sm" variant="outline-dark" onClick={() => removeLeg(index)}>
                                    Remove
                                </Button>
                            ) : undefined
                        }
                        key={index}
                        subtitle={leg.isNew ? 'New — saves with the strategy' : leg.isModified ? 'Edited — unsaved' : undefined}
                        title={`Leg ${displayIndex}`}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormInput
                                error={validationErrors[`leg_${index}_name`]}
                                label="Leg name"
                                placeholder="Enter leg name"
                                value={leg.name}
                                onChange={(e) => handleLegChange(index, 'name', e.target.value)}
                            />
                            <div>
                                <p className="text-sm text-zinc-400">Ownership and exposure</p>
                                <p className="mt-2 text-sm font-medium text-white">{getTradingStrategyLegSummary(leg)}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <FormSelect
                                label="Derivative type"
                                options={[
                                    { value: '', label: 'None (stock/crypto)' },
                                    { value: 'Option', label: 'Option' },
                                    { value: 'Future', label: 'Future' }
                                ]}
                                value={leg.derivativeType || ''}
                                onChange={(e) => {
                                    const nextDerivativeType = parseTradingStrategyLegDerivativeTypeInput(e.target.value);

                                    handleLegDerivativeTypeChange(index, nextDerivativeType);
                                }}
                            />
                            <FormSelect
                                error={validationErrors[`leg_${index}_openTransactionType`]}
                                label="Opening action"
                                options={
                                    leg.derivativeType
                                        ? [
                                              { value: 'buyToOpen', label: 'Buy to open' },
                                              { value: 'sellToOpen', label: 'Sell to open' }
                                          ]
                                        : [
                                              { value: 'buy', label: 'Buy' },
                                              { value: 'shortSell', label: 'Short sell' }
                                          ]
                                }
                                value={leg.openTransactionType}
                                onChange={(e) =>
                                    handleLegChange(
                                        index,
                                        'openTransactionType',
                                        parseTradingStrategyLegOpenTransactionTypeInput(e.target.value, leg.openTransactionType)
                                    )
                                }
                            />
                        </div>

                        {leg.derivativeType === 'Option' && (
                            <div className="mt-4">
                                <FormSelect
                                    error={validationErrors[`leg_${index}_optionType`]}
                                    label="Option type"
                                    options={[
                                        { value: '', label: 'Select Call or Put' },
                                        { value: 'call', label: 'Call' },
                                        { value: 'put', label: 'Put' }
                                    ]}
                                    value={leg.optionType ?? ''}
                                    onChange={(e) =>
                                        handleLegChange(index, 'optionType', parseTradingStrategyLegOptionTypeInput(e.target.value))
                                    }
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <FormSelect
                                error={sizeTypeError}
                                label="Position size type"
                                options={[
                                    { value: 'percentageRiskCapital', label: '% of risk capital' },
                                    { value: 'percentageValue', label: '% of position size' },
                                    ...(riskToStopEligible || usesRiskToStop
                                        ? [
                                              {
                                                  value: 'percentageRiskToStop',
                                                  label: '% of capital at risk to the stop',
                                                  disabled: !riskToStopEligible
                                              }
                                          ]
                                        : [])
                                ]}
                                value={leg.entryTransactionSizeType}
                                onChange={(e) =>
                                    handleLegChange(
                                        index,
                                        'entryTransactionSizeType',
                                        parseTradingStrategyLegEntryTransactionSizeTypeInput(e.target.value, leg.entryTransactionSizeType)
                                    )
                                }
                            />
                            <FormNumberInput
                                error={validationErrors[`leg_${index}_entryTransactionSizeValue`]}
                                label="Position size value (%)"
                                max={100}
                                min={0}
                                step={0.01}
                                value={leg.entryTransactionSizeValue}
                                onValueChange={(value) => {
                                    // A leg always carries a size, so an empty field keeps the stored value.
                                    if (value === null) return;

                                    handleLegChange(index, 'entryTransactionSizeValue', value);
                                }}
                            />
                        </div>

                        {/* Risk-to-stop sizing reads the stop captured by native 5m futures price entries. */}
                        {usesRiskToStop && riskToStopEligible && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <FormInput
                                    error={validationErrors[`leg_${index}_riskStopCaptureKey`]}
                                    label="Stop capture key"
                                    placeholder="Entry capture holding the stop, e.g. triggerLow"
                                    value={leg.riskStopCaptureKey}
                                    onChange={(e) => handleLegChange(index, 'riskStopCaptureKey', e.target.value)}
                                />
                                <FormNumberInput
                                    error={validationErrors[`leg_${index}_minimumQuantity`]}
                                    helpText="Optional. Entries sized below this many contracts are skipped; leave empty for no minimum."
                                    integer
                                    label="Minimum quantity (whole units)"
                                    max={MAXIMUM_RISK_TO_STOP_MINIMUM_QUANTITY}
                                    min={1}
                                    step={1}
                                    value={leg.minimumQuantity}
                                    onValueChange={(value) => handleLegChange(index, 'minimumQuantity', value)}
                                />
                            </div>
                        )}

                        <div className="mt-4">
                            <FormInput
                                label="Leg description"
                                placeholder="Enter leg description (optional)"
                                value={leg.description}
                                onChange={(e) => handleLegChange(index, 'description', e.target.value)}
                            />
                        </div>

                        {/* Option Buying Strategies Override - Only show for option legs */}
                        {leg.derivativeType === 'Option' && (
                            <div className="mt-4">
                                <OptionBuyingStrategySelect
                                    error={validationErrors[`leg_${index}_autoBacktestOptionBuyingStrategiesOverride`]}
                                    helpText="Leave empty to use the strategy default."
                                    label="Option buying strategies (leg override)"
                                    mode="multi"
                                    placeholder="Select option buying strategies..."
                                    value={leg.autoBacktestOptionBuyingStrategiesOverride ?? []}
                                    onChange={(selectedIds) => {
                                        handleLegChange(
                                            index,
                                            'autoBacktestOptionBuyingStrategiesOverride',
                                            selectedIds.length > 0 ? selectedIds : undefined
                                        );
                                    }}
                                />
                            </div>
                        )}
                    </Card>
                );
            })}
        </div>
    );
};

export default StrategyLegsSection;
